import { useCallback } from 'react';

import {
  BOARD_LIMIT,
  useBoards,
  useCreateBoard,
  useDeleteBoard,
  useUpdateBoardTitle,
} from '@/entities/board';
import { toBoardsErrorMessage } from '@/features/boards/helpers/boards-error-message';

export function useBoardsListData() {
  const { data: boards = [], isLoading: isBoardsLoading } = useBoards();

  const createBoardMutation = useCreateBoard();
  const deleteBoardMutation = useDeleteBoard();
  const updateBoardTitleMutation = useUpdateBoardTitle();

  const createList = useCallback(
    async (title: string) => {
      const trimmed = title.trim();
      if (!trimmed) return { ok: false as const, reason: 'Please enter a list name.' };
      if (boards.length >= BOARD_LIMIT) {
        return { ok: false as const, reason: `You can create up to ${BOARD_LIMIT} boards.` };
      }

      try {
        const createdBoard = await createBoardMutation.mutateAsync({ title: trimmed });
        return { ok: true as const, boardId: createdBoard.id };
      } catch (error) {
        return {
          ok: false as const,
          reason: toBoardsErrorMessage(error, 'Failed to create board.'),
        };
      }
    },
    [boards.length, createBoardMutation],
  );

  const deleteList = useCallback(
    async (boardId: string) => {
      await deleteBoardMutation.mutateAsync(boardId);
    },
    [deleteBoardMutation],
  );

  const renameList = useCallback(
    async (boardId: string, title: string) => {
      const trimmed = title.trim();
      if (!trimmed) return;
      await updateBoardTitleMutation.mutateAsync({ boardId, title: trimmed });
    },
    [updateBoardTitleMutation],
  );

  const isMutating =
    createBoardMutation.isPending ||
    deleteBoardMutation.isPending ||
    updateBoardTitleMutation.isPending;

  return {
    boards,
    isLoading: isBoardsLoading || isMutating,
    createList,
    deleteList,
    renameList,
  };
}
